import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import koLocale from 'date-fns/locale/ko';
import { DialogTitleClose, PaperComponent } from '../dialog';
import { createPreviewObjectUrl } from './SubMatFolderFileActions';
import * as subMatFolderActions from '../../store/subMatFolder';
import { dateFormat } from '../../utils';
import { SubMatExpiryStatusChip } from './SubMatFolderExpiryStatus';

/**
 * 미리보기와 분리된 만료일 설정 다이얼로그 (PDF + endDate/validYN).
 * 국세/지방세는 사용자가 taxType 선택 후 자동판독.
 */
const SubMatFolderExpiryDialog = ({
  open,
  file,
  folderId,
  onClose,
  onSaved,
}) => {
  const [previewUrl, setPreviewUrl] = useState('');
  const previewUrlRef = useRef('');
  const [endDate, setEndDate] = useState(null);
  const [validYN, setValidYN] = useState(false);
  const [taxType, setTaxType] = useState('');
  const [busy, setBusy] = useState(false);
  const [extractBusy, setExtractBusy] = useState(false);
  const [error, setError] = useState('');

  const fid = folderId || file?.folderId;

  useEffect(() => {
    if (!open || !file?.id || !file?.documentPath) {
      return undefined;
    }
    let cancelled = false;
    setError('');
    setValidYN(file.validYN === true);
    setEndDate(file.endDate ? new Date(file.endDate) : null);
    setTaxType('');
    (async () => {
      try {
        const url = await createPreviewObjectUrl(file.documentPath);
        if (cancelled) {
          window.URL.revokeObjectURL(url);
          return;
        }
        if (previewUrlRef.current) {
          window.URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
      } catch (e) {
        if (!cancelled) setError(e.message || '미리보기를 열 수 없습니다.');
      }
    })();
    return () => {
      cancelled = true;
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = '';
      }
      setPreviewUrl('');
    };
    // 열릴 때·파일 전환 시에만 초기화 (grid row 참조 변동으로 입력 중 리셋 방지)
  }, [open, file?.id, file?.documentPath]);

  const handleValidToggle = (checked) => {
    setValidYN(checked);
    if (checked) setEndDate(null);
  };

  const handleExtract = async () => {
    if (!fid || !file?.id || !taxType) return;
    setExtractBusy(true);
    setError('');
    try {
      const parsed = await subMatFolderActions.extractFileExpiryDirect(fid, file.id, taxType);
      if (parsed) {
        setEndDate(new Date(parsed));
        setValidYN(false);
      } else {
        setError('만료일을 자동으로 판독하지 못했습니다. 직접 입력해 주세요.');
      }
    } catch (e) {
      setError(e?.serverResponse?.message || e.message || '자동판독에 실패했습니다.');
    } finally {
      setExtractBusy(false);
    }
  };

  const handleSave = async () => {
    if (!fid || !file?.id) return;
    if (!validYN && !endDate) {
      setError('만료일을 입력하거나 「만료일 없음」을 선택하세요.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payloadEnd = validYN
        ? null
        : (endDate instanceof Date
          ? dateFormat(endDate.getDateWithEndHours ? endDate.getDateWithEndHours() : endDate)
          : endDate);
      const saved = await subMatFolderActions.updateFileExpiryDirect(fid, file.id, {
        endDate: payloadEnd,
        validYN: Boolean(validYN),
      });
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      setError(e?.serverResponse?.message || e.message || '저장에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason !== 'backdropClick' && !busy) onClose?.();
      }}
      fullWidth
      maxWidth="lg"
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      // DatePicker 팝업·PDF iframe 과 Dialog 포커스 트랩이 겹치면 첫 클릭이 무시됨
      disableEnforceFocus
      disableRestoreFocus
    >
      <DialogTitleClose
        id="draggable-dialog-title"
        onClose={() => !busy && onClose?.()}
      >
        만료일 설정 — {file?.fileName || ''}
      </DialogTitleClose>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, minHeight: 480, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Box sx={{ flex: 1.4, minWidth: 280, minHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {previewUrl ? (
              <iframe
                title="서류 미리보기"
                src={previewUrl}
                width="100%"
                height="100%"
                tabIndex={-1}
                style={{ border: 0, minHeight: 420 }}
              />
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">미리보기 준비 중…</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2">현재 상태</Typography>
              <SubMatExpiryStatusChip file={{ ...file, endDate, validYN }} />
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={koLocale}>
              <DatePicker
                label="서류 만료일"
                value={validYN ? null : endDate}
                onChange={(v) => {
                  setValidYN(false);
                  setEndDate(v);
                }}
                disabled={validYN || busy}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    onMouseDown={(e) => {
                      // DataGrid 등 상위 포커스 가로채기 방지
                      e.stopPropagation();
                      params.onMouseDown?.(e);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      params.onClick?.(e);
                    }}
                  />
                )}
                inputFormat="yyyy-MM-dd"
                OpenPickerButtonProps={{
                  onMouseDown: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  },
                }}
                PopperProps={{
                  sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
                  onMouseDown: (e) => e.stopPropagation(),
                }}
              />
            </LocalizationProvider>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={validYN}
                  onChange={(e) => handleValidToggle(e.target.checked)}
                  disabled={busy}
                />
              )}
              label="만료일 없음"
            />
            <Typography variant="caption" color="text.secondary">
              국세·지방세 납세증명서는 아래에서 유형을 고른 뒤 자동판독할 수 있습니다.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label="자동판독 유형"
                value={taxType}
                onChange={(e) => setTaxType(e.target.value)}
                sx={{ minWidth: 160 }}
                disabled={busy || extractBusy}
              >
                <MenuItem value="">선택</MenuItem>
                <MenuItem value="NATIONAL_TAX">국세 납세증명서</MenuItem>
                <MenuItem value="LOCAL_TAX">지방세 납세증명서</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExtract}
                disabled={!taxType || busy || extractBusy}
              >
                {extractBusy ? '판독 중…' : '만료일 자동판독'}
              </Button>
            </Box>
            {error && (
              <Typography variant="body2" color="error">{error}</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.()} disabled={busy}>취소</Button>
        <Button variant="contained" onClick={handleSave} disabled={busy}>
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubMatFolderExpiryDialog;
